import { useTranslation } from "react-i18next";

interface DriftItem {
  key: string;
  label: string;
  actualCents: number;
  targetCents?: number;
  driftCents?: number;
  driftPp?: number;
  underwater?: true;
}

export interface DriftTableProps {
  items: DriftItem[];
  totalCents: number;
  formatEur: (cents: number) => string;
  /** Klick auf eine Zeile (Sub- oder Top-Klasse) — fuer Drill-Down-Modal. */
  onRowClick?: (key: string) => void;
}

const HIGHLIGHT_PP = 5;

export function DriftTable({ items, totalCents, formatEur, onRowClick }: DriftTableProps) {
  const { t } = useTranslation();

  // Einheitliche Klassen: alle numerischen Zellen + Header bekommen
  // whitespace-nowrap, damit Werte nicht ueber zwei Zeilen brechen.
  const numHead =
    "px-3 py-2 text-right font-semibold text-slate-800 whitespace-nowrap";
  const numCell = "px-3 py-2 text-right font-mono whitespace-nowrap";

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto text-sm border-collapse">
        <thead>
          <tr className="text-left border-b-2 border-slate-300 text-xs text-slate-700 bg-slate-100">
            <th className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">
              {t("allocation.column.class")}
            </th>
            <th className={numHead}>{t("allocation.column.ist_eur")}</th>
            <th className={numHead}>{t("allocation.column.ist_pct")}</th>
            <th className={numHead}>{t("allocation.column.soll_eur")}</th>
            {/* Drift-Spalte kombiniert pp + EUR in einer Zelle; spart Breite,
                ohne Info zu verlieren. Aktion-Spalte komplett entfernt — sie
                war nur die Vorzeichen-Umkehrung von Drift (EUR). */}
            <th className={numHead}>{t("allocation.column.drift_combined", "Drift")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const istPct = totalCents > 0 ? (item.actualCents / totalCents) * 100 : 0;
            const driftClass =
              item.driftPp !== undefined && Math.abs(item.driftPp) >= HIGHLIGHT_PP
                ? item.driftPp > 0
                  ? "text-amber-700 font-semibold"
                  : "text-red-700 font-semibold"
                : "text-slate-700";
            return (
              <tr
                key={item.key}
                className={`border-b hover:bg-slate-50 ${onRowClick ? "cursor-pointer" : ""}`}
                onClick={onRowClick ? () => onRowClick(item.key) : undefined}
              >
                <td className="px-3 py-2 whitespace-nowrap">
                  {item.label}
                  {item.underwater && (
                    <span className="ml-2 text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                      {t("allocation.underwater_badge")}
                    </span>
                  )}
                </td>
                <td className={numCell}>{formatEur(item.actualCents)}</td>
                <td className={numCell}>{istPct.toFixed(1)} %</td>
                <td className={`${numCell} text-slate-500`}>
                  {item.targetCents !== undefined ? formatEur(item.targetCents) : "—"}
                </td>
                <td className={`${numCell} ${driftClass}`}>
                  {item.driftCents !== undefined ? (
                    <div>
                      <div>{formatEur(item.driftCents)}</div>
                      <div className="text-xs font-normal">
                        {item.driftPp !== undefined ? `${item.driftPp.toFixed(1)} pp` : ""}
                      </div>
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
